import React, { useState } from 'react';
import { SearchedOpinion } from '../types';
import { OpinionDetailsModal } from './OpinionDetailsModal';

interface SearchedOpinionsListProps {
  opinions: SearchedOpinion[];
  searchExecutionTimeMs?: number;
  parameterGenerationTimeMs?: number;
  embeddingGenerationTimeMs?: number;
}

export const SearchedOpinionsList: React.FC<SearchedOpinionsListProps> = ({ 
  opinions
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailedOpinions, setDetailedOpinions] = useState<SearchedOpinion[]>([]);

  if (!opinions || opinions.length === 0) {
    return null;
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyAllIds = () => {
    const ids = opinions.map(opinion => opinion.id).join(', ');
    copyToClipboard(ids);
  };

  const copyAllContent = () => {
    const content = opinions.map(opinion => {
      const parts = [
        `Opinion ID: ${opinion.id}`,
        opinion.title ? `Title: ${opinion.title}` : '',
        opinion.snippet ? `Snippet: ${opinion.snippet}` : '',
        opinion.score ? `Score: ${opinion.score.toFixed(2)}` : '',
        `Found: ${new Date(opinion.searchedAt).toLocaleString()}`,
        `Link: ${opinion.link}`
      ].filter(Boolean);
      return parts.join('\n');
    }).join('\n\n---\n\n');
    copyToClipboard(content);
  };

  const copyOpinionContent = (opinion: SearchedOpinion) => {
    const parts = [
      `Opinion ID: ${opinion.id}`,
      opinion.title ? `Title: ${opinion.title}` : '',
      opinion.snippet ? `Snippet: ${opinion.snippet}` : '',
      opinion.score ? `Score: ${opinion.score.toFixed(2)}` : '',
      `Found: ${new Date(opinion.searchedAt).toLocaleString()}`,
      `Link: ${opinion.link}`
    ].filter(Boolean);
    const content = parts.join('\n');
    copyToClipboard(content);
  };

  const handleViewDetails = () => {
    // Use opinions directly from SignalR events (already have paragraphs)
    setDetailedOpinions(opinions);
    setIsModalOpen(true);
  };

  return (
    <>
      <div data-component="SearchedOpinionsList" className="relative">
        {/* Top-right corner buttons */}
        <div className="absolute top-0 right-0 flex gap-2 z-10">
          <button
            onClick={handleViewDetails}
            className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
            title="View detailed information for all opinions"
          >
            View Details
          </button>
          <button
            onClick={copyAllIds}
            className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            title="Copy all opinion IDs"
          >
            Copy IDs
          </button>
          <button
            onClick={copyAllContent}
            className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            title="Copy all opinion content"
          >
            Copy Content
          </button>
        </div>

      <div className="space-y-2 max-h-48 overflow-y-auto pt-8">
        {opinions.map((opinion, index) => (
          <div key={opinion.id || index} className="relative p-2 bg-white rounded border border-blue-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <a
                    href={opinion.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                  >
                    Opinion ID: {opinion.id}
                  </a>
                  <a
                    href={`https://happy-sand-0c513521e.2.azurestaticapps.net/opinions/${opinion.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-lg font-bold transition-colors"
                    title="View opinion details"
                  >
                    §
                  </a>
                  {opinion.polarity && (
                    <span className={`px-2 py-0.5 rounded font-medium text-xs ${
                      opinion.polarity === 'contrary' ? 'bg-red-100 text-red-700' :
                      opinion.polarity === 'mixed' ? 'bg-amber-100 text-amber-700' :
                      opinion.polarity === 'supporting' ? 'bg-emerald-100 text-emerald-700' : ''
                    }`}>
                      {opinion.polarity === 'contrary' ? '⚖️ Contrary' :
                       opinion.polarity === 'mixed' ? '⚖️ Mixed' :
                       opinion.polarity === 'supporting' ? '✓ Supporting' : ''}
                    </span>
                  )}
                </div>
                {opinion.title && (
                  <div className="text-sm text-gray-700 mt-1">{opinion.title}</div>
                )}
                {opinion.snippet && (
                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {opinion.snippet}
                  </div>
                )}
              </div>
              {opinion.score && (
                <div className="ml-2 text-xs text-gray-500">
                  Score: {opinion.score.toFixed(2)}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="text-xs text-gray-400">
                Found: {new Date(opinion.searchedAt).toLocaleString()}
              </div>
              {/* Individual copy button - bottom right */}
              <button
                onClick={() => copyOpinionContent(opinion)}
                className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                title="Copy this opinion"
              >
                Copy
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>

    <OpinionDetailsModal
      opinions={detailedOpinions}
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
    />
  </>
  );
};